export type PrimitiveExecution = {
  id?: string;
  timestamp: number;
  side: 'BUY' | 'SELL' | 'buy' | 'sell';
  qty: number;
  price: number;
  fee?: number;
  originalIndex?: number;
};

export type PrimitivePhaseDirection = 'long' | 'short';

export type PrimitivePhaseOp = {
  time: number;
  seq: number;
  opOrder: number;
  kind: 'open' | 'close';
  qty: number;
  price: number;
};

export type PrimitivePhase = {
  id: string;
  direction: PrimitivePhaseDirection;
  startTime: number;
  endTime: number | null;
  openQty: number;
  actualGross: number;
  totalFees: number;
  ops: PrimitivePhaseOp[];
};

export type PrimitiveOpenLot = {
  qty: number;
  price: number;
  feePerUnit: number;
  phaseId: string;
};

export type PrimitiveNormalizedExecution = {
  sourceId: string;
  timestamp: number;
  side: 'buy' | 'sell';
  qty: number;
  price: number;
  fee: number;
  originalIndex: number;
};

export type PrimitivePositionState = {
  orderedExecutionIds: string[];
  applied: PrimitiveNormalizedExecution[];
  phases: PrimitivePhase[];
  openLongLots: PrimitiveOpenLot[];
  openShortLots: PrimitiveOpenLot[];
  netQuantity: number;
  side: 'long' | 'short' | 'flat';
  avgEntryPrice: number | null;
  breakEvenPrice: number | null;
  realizedGross: number;
  realizedFeesPaid: number;
  openFeesPaid: number;
  totalFeesPaid: number;
  unrealizedGross: number;
  unrealizedPnl: number;
  realizedPnl: number;
  totalPnl: number;
};

function normalizeSide(side: string): 'buy' | 'sell' {
  return String(side).toLowerCase().startsWith('s') ? 'sell' : 'buy';
}

export function normalizeAndSortExecutions(executions: PrimitiveExecution[]): PrimitiveNormalizedExecution[] {
  return [...(executions || [])]
    .map((execution, index) => {
      const timestamp = Number(execution.timestamp);
      const qty = Math.abs(Number(execution.qty) || 0);
      const price = Number(execution.price);
      const fee = Number(execution.fee || 0);
      const originalIndex = Number.isFinite(Number(execution.originalIndex)) ? Number(execution.originalIndex) : index;

      return {
        sourceId: String(execution.id ?? `exec-${index}`),
        timestamp,
        side: normalizeSide(execution.side),
        qty,
        price,
        fee: Number.isFinite(fee) ? fee : 0,
        originalIndex,
      } satisfies PrimitiveNormalizedExecution;
    })
    .filter((execution) => Number.isFinite(execution.timestamp) && execution.qty > 0 && Number.isFinite(execution.price))
    .sort((a, b) => {
      const dt = a.timestamp - b.timestamp;
      return dt !== 0 ? dt : (a.originalIndex - b.originalIndex);
    });
}

export function computeDeterministicPositionState(input: {
  executions: PrimitiveExecution[];
  multiplier: number;
  markPrice?: number | null;
}): PrimitivePositionState {
  const multiplier = Number.isFinite(Number(input.multiplier)) ? Number(input.multiplier) : 1;
  const applied = normalizeAndSortExecutions(input.executions);

  const phaseMap = new Map<string, PrimitivePhase>();
  const openLongLots: PrimitiveOpenLot[] = [];
  const openShortLots: PrimitiveOpenLot[] = [];

  let activeLongPhaseId: string | null = null;
  let activeShortPhaseId: string | null = null;
  let phaseCounter = 0;
  let realizedGross = 0;
  let realizedFeesPaid = 0;

  const ensurePhase = (direction: PrimitivePhaseDirection, time: number): PrimitivePhase => {
    const activeId = direction === 'long' ? activeLongPhaseId : activeShortPhaseId;
    if (activeId && phaseMap.has(activeId)) return phaseMap.get(activeId)!;

    const phase: PrimitivePhase = {
      id: `phase-${++phaseCounter}`,
      direction,
      startTime: time,
      endTime: null,
      openQty: 0,
      actualGross: 0,
      totalFees: 0,
      ops: [],
    };
    phaseMap.set(phase.id, phase);
    if (direction === 'long') activeLongPhaseId = phase.id;
    else activeShortPhaseId = phase.id;
    return phase;
  };

  const closePhaseIfFlat = (phaseId: string, time: number) => {
    const phase = phaseMap.get(phaseId);
    if (!phase || phase.openQty > 0 || phase.endTime != null) return;
    phase.endTime = time;
    if (phase.direction === 'long' && activeLongPhaseId === phase.id) activeLongPhaseId = null;
    if (phase.direction === 'short' && activeShortPhaseId === phase.id) activeShortPhaseId = null;
  };

  for (let seq = 0; seq < applied.length; seq += 1) {
    const execution = applied[seq];
    let remainingQty = execution.qty;
    const feePerUnit = execution.qty > 0 ? execution.fee / execution.qty : 0;

    if (execution.side === 'sell') {
      while (remainingQty > 0 && openLongLots.length > 0) {
        const lot = openLongLots[0];
        const matched = Math.min(remainingQty, lot.qty);
        const closingFee = feePerUnit * matched;
        const openingFee = lot.feePerUnit * matched;
        const gross = (execution.price - lot.price) * matched * multiplier;
        const phase = phaseMap.get(lot.phaseId)!;

        phase.actualGross += gross;
        phase.totalFees += closingFee;
        phase.openQty -= matched;
        phase.ops.push({ time: execution.timestamp, seq, opOrder: 0, kind: 'close', qty: matched, price: execution.price });

        realizedGross += gross;
        realizedFeesPaid += openingFee + closingFee;

        lot.qty -= matched;
        remainingQty -= matched;
        if (lot.qty <= 0) openLongLots.shift();
        closePhaseIfFlat(phase.id, execution.timestamp);
      }

      if (remainingQty > 0) {
        const phase = ensurePhase('short', execution.timestamp);
        phase.openQty += remainingQty;
        phase.totalFees += feePerUnit * remainingQty;
        phase.ops.push({ time: execution.timestamp, seq, opOrder: 1, kind: 'open', qty: remainingQty, price: execution.price });
        openShortLots.push({ qty: remainingQty, price: execution.price, feePerUnit, phaseId: phase.id });
      }
    } else {
      while (remainingQty > 0 && openShortLots.length > 0) {
        const lot = openShortLots[0];
        const matched = Math.min(remainingQty, lot.qty);
        const closingFee = feePerUnit * matched;
        const openingFee = lot.feePerUnit * matched;
        const gross = (lot.price - execution.price) * matched * multiplier;
        const phase = phaseMap.get(lot.phaseId)!;

        phase.actualGross += gross;
        phase.totalFees += closingFee;
        phase.openQty -= matched;
        phase.ops.push({ time: execution.timestamp, seq, opOrder: 0, kind: 'close', qty: matched, price: execution.price });

        realizedGross += gross;
        realizedFeesPaid += openingFee + closingFee;

        lot.qty -= matched;
        remainingQty -= matched;
        if (lot.qty <= 0) openShortLots.shift();
        closePhaseIfFlat(phase.id, execution.timestamp);
      }

      if (remainingQty > 0) {
        const phase = ensurePhase('long', execution.timestamp);
        phase.openQty += remainingQty;
        phase.totalFees += feePerUnit * remainingQty;
        phase.ops.push({ time: execution.timestamp, seq, opOrder: 1, kind: 'open', qty: remainingQty, price: execution.price });
        openLongLots.push({ qty: remainingQty, price: execution.price, feePerUnit, phaseId: phase.id });
      }
    }
  }

  const openLongQty = openLongLots.reduce((sum, lot) => sum + lot.qty, 0);
  const openShortQty = openShortLots.reduce((sum, lot) => sum + lot.qty, 0);
  const netQuantity = openLongQty - openShortQty;
  const side: 'long' | 'short' | 'flat' = netQuantity > 0 ? 'long' : netQuantity < 0 ? 'short' : 'flat';

  const activeLots = side === 'long' ? openLongLots : side === 'short' ? openShortLots : [];
  const activeQty = Math.abs(netQuantity);
  const avgEntryPrice = activeQty > 0
    ? activeLots.reduce((sum, lot) => sum + (lot.price * lot.qty), 0) / activeQty
    : null;

  const openFeesPaid = activeLots.reduce((sum, lot) => sum + (lot.feePerUnit * lot.qty), 0);
  const totalFeesPaid = realizedFeesPaid + openFeesPaid;

  const markPrice = Number.isFinite(Number(input.markPrice)) ? Number(input.markPrice) : null;
  let unrealizedGross = 0;

  if (markPrice != null) {
    if (side === 'long') {
      unrealizedGross = activeLots.reduce((sum, lot) => sum + ((markPrice - lot.price) * lot.qty * multiplier), 0);
    } else if (side === 'short') {
      unrealizedGross = activeLots.reduce((sum, lot) => sum + ((lot.price - markPrice) * lot.qty * multiplier), 0);
    }
  }

  const realizedPnl = realizedGross - realizedFeesPaid;
  const unrealizedPnl = side === 'flat' ? 0 : unrealizedGross - openFeesPaid;
  const totalPnl = realizedPnl + unrealizedPnl;

  let breakEvenPrice: number | null = null;
  if (side !== 'flat' && avgEntryPrice != null && activeQty > 0) {
    const feePerContract = openFeesPaid / Math.max(1, activeQty * multiplier);
    breakEvenPrice = side === 'long' ? avgEntryPrice + feePerContract : avgEntryPrice - feePerContract;
  }

  return {
    orderedExecutionIds: applied.map((execution) => execution.sourceId),
    applied,
    phases: [...phaseMap.values()].sort((a, b) => a.startTime - b.startTime),
    openLongLots,
    openShortLots,
    netQuantity,
    side,
    avgEntryPrice,
    breakEvenPrice,
    realizedGross,
    realizedFeesPaid,
    openFeesPaid,
    totalFeesPaid,
    unrealizedGross,
    unrealizedPnl,
    realizedPnl,
    totalPnl,
  };
}
